import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SecurityContext,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-featured-description-card',
  templateUrl: './featured-description-card.template.html',
  styleUrls: ['./featured-description-card.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, NgIf, NgFor, AsyncPipe],
})
export class FeaturedDescriptionCardComponent implements OnChanges, OnDestroy {
  @Input() public product?: Product | null;

  public readonly lessThan$: Observable<ScreenSizeInfoState>;
  public readMore: boolean;

  private readonly descriptionSubject: BehaviorSubject<SafeHtml>;
  public readonly description$: Observable<SafeHtml>;
  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  private readonly specsSubject: BehaviorSubject<Array<{ key: string; value: string }>>;
  public readonly specs$: Observable<Array<{ key: string; value: string }>>;

  public readonly contentExpanded$: Observable<boolean>;
  private readonly contentSubject: BehaviorSubject<boolean>;

  constructor(
    private readonly domSanitizer: DomSanitizer,
    private readonly translateService: TranslocoService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery
  ) {
    this.destroySubject = new Subject<void>();
    this.subscriptions = new Subscription();

    this.contentSubject = new BehaviorSubject('' as any);
    this.contentExpanded$ = this.contentSubject.asObservable();

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.readMore = false;

    if (this.akitaScreenQuery.lessThan.sm) {
      this.contentSubject.next(false);
    } else {
      this.contentSubject.next(true);
    }

    this.descriptionSubject = new BehaviorSubject('' as any);
    this.description$ = this.descriptionSubject.asObservable();

    this.specsSubject = new BehaviorSubject([] as any);
    this.specs$ = this.specsSubject.asObservable();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.product) {
      let parsed = this.domSanitizer.bypassSecurityTrustHtml('');

      const longDesc = `${this.product?.description || ''}`;
      if (longDesc) {
        parsed = this.domSanitizer.bypassSecurityTrustHtml(longDesc);
        parsed = this.parseLong(longDesc);
      } else {
        const cleanDescription = this.cleanDescription(this.product);
        parsed = this.parseDescription(cleanDescription);
      }

      this.descriptionSubject.next(parsed);
    }
  }

  private cleanDescription(product?: Product | null): string {
    let description = '';
    if (product?.description) {
      description = `${product.description}`.trim();
    } else {
      description = this.placeholderDescription;
    }
    return description;
  }

  private parseDescription(text?: string | null): SafeHtml {
    let description = `${text || ''}`;
    const newSpecs = new Array(0);

    if (description && description !== this.placeholderDescription) {
      const lines = description.replace(/\s·\s/g, '\n').split(/\n/g);

      let addBreak = false;
      const newLines = new Array(0);
      for (const line of lines) {
        const delimIndex = line.indexOf(':');
        if (delimIndex !== -1) {
          addBreak = true;

          const label = line.slice(0, delimIndex);
          const value = line.slice(delimIndex + 1);
          newSpecs.push({
            key: label,
            value: value,
          });
        } else if (line) {
          if (addBreak) {
            newLines.push(`<br />`);
          }
          addBreak = false;

          newLines.push(`<span class="flex align-center">${line}</span>`);
        } else {
          newLines.push(`<br />`);
        }
      }

      description =
        this.domSanitizer.sanitize(SecurityContext.HTML, newLines.join('')) || '';
    } else {
      description = this.domSanitizer.sanitize(SecurityContext.HTML, description) || '';
    }

    this.specsSubject.next(newSpecs);
    return this.domSanitizer.bypassSecurityTrustHtml(description);
  }

  private parseLong(text?: string | null): SafeHtml {
    let description = `${text || ''}`;

    if (description && description !== this.placeholderDescription) {
      const lines = description.split(/\n/g);

      const newLines = new Array(0);
      for (const line of lines) {
        if (line) {
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          if (line.length < 70) {
            newLines.push(`<h4 class="flex align-center">${line}</h4>`);
          } else {
            newLines.push(`<span class="flex align-center">${line}</span>`);
          }
        } else {
          newLines.push(`<br />`);
        }
      }

      description =
        this.domSanitizer.sanitize(SecurityContext.HTML, newLines.join('')) || '';
    } else {
      description = this.domSanitizer.sanitize(SecurityContext.HTML, description) || '';
    }

    return this.domSanitizer.bypassSecurityTrustHtml(description);
  }

  private get placeholderDescription(): string {
    let listingTitle = '';
    const message = this.translateService.translate('msg_empty_description_long');

    if (this.product?.title) {
      listingTitle = this.product.title;
    }

    return `${message} ${listingTitle}`;
  }

  public switchExpanded(): void {
    if (this.contentSubject.value) {
      this.contentSubject.next(false);
    } else {
      this.contentSubject.next(true);
    }
  }

  public trackByKeyVal(index: number, value?: { key: string; value: string }): string {
    return `${value?.key || index}${value?.value || index}`;
  }

  public collapseAndScroll(): void {
    this.readMore = !this.readMore;
    if (
      this.akitaRouterQuery.isBrowser &&
      window.document &&
      window.document.getElementById
    ) {
      const scrollElement = window.document.getElementById('mainAppScroll');
      if (scrollElement) {
        if (scrollElement.scrollTo) {
          scrollElement.scrollTo({
            top: 300,
            left: 0,
            behavior: 'smooth',
          });
        } else {
          scrollElement.scrollTop = 300;
        }
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
